import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';

import { ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import type { PipelineFeatureProperties } from '@/components/map/utils/pipelineFeatureProperties';
import { PIPELINE_SOURCE_ID } from '@/hooks/usePipelinesLayer';
import { createFeatureHoverTween } from '@/utils/featureHoverTween';

export interface PipelineInteractionState {
    hoveredPipeline: PipelineFeatureProperties | null;
    /** Geographic [lng, lat] where the pipe was hovered — the MapLibre `<Popup>` anchor. */
    hoveredPipelineLngLat: [number, number] | null;
}

/**
 * Tracks the pipeline feature under the cursor so MapView can show an informational
 * hover popup (PipelinePopup) with the pipe's type and length.
 *
 * Uses `mousemove` (not `mouseenter`) on the pipelines layer so the topmost feature is
 * resolved when lines overlap, with a ref-based id guard that avoids redundant state
 * updates while the cursor stays over the same pipe. The popup is anchored to the LngLat
 * where the pipe was first hovered; the MapLibre `<Popup>` projects and tracks that point
 * itself, so no screen-pixel bookkeeping is needed here.
 *
 * Hover only — no selection or click behaviour. Re-attaches its listeners on
 * `style.load` because a base-map switch recreates all layers. Safe when the map is
 * not ready or the layer does not exist.
 */
export function usePipelineInteractions(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
): PipelineInteractionState {
    const [hoveredPipeline, setHoveredPipeline] = useState<PipelineFeatureProperties | null>(null);
    const [hoveredLngLat, setHoveredLngLat] = useState<[number, number] | null>(null);

    const hoveredIdRef = useRef<string | null>(null);

    useEffect(() => {
        if (!isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const hoverTween = createFeatureHoverTween(mlMap, PIPELINE_SOURCE_ID);

        const clearHover = () => {
            if (hoveredIdRef.current === null) return;
            hoveredIdRef.current = null;
            hoverTween.setHovered(null);
            setHoveredPipeline(null);
            setHoveredLngLat(null);
        };

        const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
            // No hover while the camera moves — a card created mid-drag/zoom/fly would
            // pan with the map, anchored to a point sliding out from under the cursor.
            if (mlMap.isMoving()) return;
            const feature = e.features?.[0];
            if (!feature) return;

            const props = feature.properties as unknown as PipelineFeatureProperties;
            const id = props.id;
            // No state update while the cursor stays over the same pipe.
            if (id === hoveredIdRef.current) return;

            hoveredIdRef.current = id;
            hoverTween.setHovered(id);
            setHoveredPipeline(props);
            setHoveredLngLat([e.lngLat.lng, e.lngLat.lat]);
        };

        // Any camera movement (drag, zoom, fly-to, ease-to) dismisses the hover card and
        // ramps the tween back down; the isMoving() guard above keeps it dismissed until
        // the movement ends, after which the next mousemove recreates hover normally.
        const handleMoveStart = () => {
            clearHover();
        };

        // Registered once — delegated layer listeners re-resolve their layer ids on every
        // event, so they survive a style reload. Re-registering them on `style.load` would
        // duplicate them: MapLibre dedupes plain listeners but not delegated ones.
        mlMap.on('mousemove', ORBIT_LAYER_IDS.pipelines, handleMouseMove);
        mlMap.on('mouseleave', ORBIT_LAYER_IDS.pipelines, clearHover);
        mlMap.on('movestart', handleMoveStart);

        return () => {
            hoverTween.destroy();
            mlMap.off('mousemove', ORBIT_LAYER_IDS.pipelines, handleMouseMove);
            mlMap.off('mouseleave', ORBIT_LAYER_IDS.pipelines, clearHover);
            mlMap.off('movestart', handleMoveStart);
        };
    }, [isMapLoaded, mapRef]);

    return { hoveredPipeline, hoveredPipelineLngLat: hoveredLngLat };
}
